/**
 * Reads the field-of-view sweep out of a stored calibration report so it can be drawn as a grid.
 *
 * The report is kept as an opaque JSON string on CalibrationReport.report — it is the device's own
 * DeviceCalibrationReportJson, serialized by Newtonsoft with default settings and never round-tripped
 * through a generated model. That means PascalCase keys and, importantly, non-finite doubles written as
 * the quoted strings "NaN" / "Infinity": an undetected cell arrives as `"SnrDb": "NaN"`, which JSON.parse
 * hands back as a string. Everything below therefore treats the payload as untrusted shape and coerces.
 */

/** One grid cell as it appears in the stored payload, after coercion. */
export type FovSweepPoint = {
    /** Commanded gimbal angles — the grid coordinates. */
    azimuthDegrees: number;
    elevationDegrees: number;
    detected: boolean;
    /** NaN when the cell was never detected. */
    snrDb: number;
    noiseDb: number;
    measuredRangeMeters: number;
    measuredAzimuthDegrees: number;
    measuredElevationDegrees: number;
    frameCount: number;
};

export type FovSweepGrid = {
    points: FovSweepPoint[];
    /** Sorted unique commanded angles, so the grid can be laid out without assuming a step. */
    azimuths: number[];
    elevations: number[];
    /** Keyed "az,el" for O(1) cell lookup while rendering. */
    byCell: Map<string, FovSweepPoint>;
    /** Cells the device's own analysis flagged, keyed the same way. */
    deadCells: Set<string>;
    /**
     * Cells where the reflector was seen but not where it is — a second echo winning the range gate.
     * Kept apart from dead cells because the cause is different: this is the chamber, not the unit.
     */
    ambiguousCells: Set<string>;
    /**
     * Azimuth half-width the acceptance checks were applied over. Undefined on reports written before the
     * grading window existed, where the whole sweep was graded.
     */
    evaluationAzimuthLimitDegrees?: number;
    peakSnrDb: number;
    /** Commanded angles of the cell the device picked as the beam peak. */
    boresightAzimuthDegrees: number;
    boresightElevationDegrees: number;
    detectionThresholdDb: number;
};

export const cellKey = (azimuth: number, elevation: number) => `${azimuth},${elevation}`;

/**
 * Coerces a value that should be a double. Newtonsoft writes NaN and the infinities as quoted strings,
 * so a bare Number() would turn "NaN" into NaN correctly but "Infinity" into NaN too — both are equally
 * unusable as a measurement, so they collapse to NaN deliberately.
 */
const toNumber = (value: unknown): number => {
    if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
    if (typeof value === "string") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : NaN;
    }
    return NaN;
};

const toPoint = (raw: Record<string, unknown>): FovSweepPoint => ({
    azimuthDegrees: toNumber(raw.AzimuthDegrees),
    elevationDegrees: toNumber(raw.ElevationDegrees),
    detected: raw.Detected === true,
    snrDb: toNumber(raw.SnrDb),
    noiseDb: toNumber(raw.NoiseDb),
    measuredRangeMeters: toNumber(raw.MeasuredRangeMeters),
    measuredAzimuthDegrees: toNumber(raw.MeasuredAzimuthDegrees),
    measuredElevationDegrees: toNumber(raw.MeasuredElevationDegrees),
    frameCount: Math.trunc(toNumber(raw.FrameCount)) || 0,
});

const uniqueSorted = (values: number[]) => Array.from(new Set(values)).sort((a, b) => a - b);

/**
 * Pulls the sweep grid out of a stored report payload.
 *
 * Returns undefined rather than throwing for any report that has no usable sweep — an older report, a
 * run that failed before the sweep, or a payload that is not JSON at all. The caller renders nothing in
 * that case, which is the right outcome for a history view that has to show every run it has.
 */
export const parseFovSweepGrid = (reportJson: string | undefined): FovSweepGrid | undefined => {
    if (!reportJson) return undefined;

    let parsed: unknown;
    try {
        parsed = JSON.parse(reportJson);
    } catch {
        return undefined;
    }

    const fov = (parsed as Record<string, unknown> | null)?.FieldOfView as Record<string, unknown> | undefined;
    const rawPoints = fov?.Points;
    if (!Array.isArray(rawPoints) || rawPoints.length === 0) return undefined;

    const points = rawPoints
        .filter((p): p is Record<string, unknown> => typeof p === "object" && p !== null)
        .map(toPoint)
        // A cell whose commanded angles did not survive has no place on the grid to be drawn at.
        .filter((p) => Number.isFinite(p.azimuthDegrees) && Number.isFinite(p.elevationDegrees));

    if (points.length === 0) return undefined;

    const byCell = new Map<string, FovSweepPoint>();
    points.forEach((p) => byCell.set(cellKey(p.azimuthDegrees, p.elevationDegrees), p));

    const keysOf = (raw: unknown): Set<string> => {
        const keys = new Set<string>();
        if (!Array.isArray(raw)) return keys;

        raw.filter((p): p is Record<string, unknown> => typeof p === "object" && p !== null).forEach((p) => {
            const azimuth = toNumber(p.AzimuthDegrees);
            const elevation = toNumber(p.ElevationDegrees);
            if (Number.isFinite(azimuth) && Number.isFinite(elevation))
                keys.add(cellKey(azimuth, elevation));
        });

        return keys;
    };

    const evaluationLimit = toNumber(fov?.EvaluationAzimuthLimitDegrees);

    return {
        points,
        azimuths: uniqueSorted(points.map((p) => p.azimuthDegrees)),
        // Descending so positive elevation draws at the top, the way the beam is actually pointed.
        elevations: uniqueSorted(points.map((p) => p.elevationDegrees)).reverse(),
        byCell,
        deadCells: keysOf(fov?.DeadCells),
        ambiguousCells: keysOf(fov?.AmbiguousCells),
        evaluationAzimuthLimitDegrees: Number.isFinite(evaluationLimit) ? evaluationLimit : undefined,
        peakSnrDb: toNumber(fov?.PeakSnrDb),
        boresightAzimuthDegrees: toNumber(fov?.BoresightAzimuthOffsetDegrees),
        boresightElevationDegrees: toNumber(fov?.BoresightElevationOffsetDegrees),
        detectionThresholdDb: toNumber(fov?.DetectionThresholdDb),
    };
};

/**
 * Absolute azimuth error for a cell: how far the angle the radar reported sits from the angle the
 * gimbal was actually commanded to.
 *
 * Deliberately the raw difference rather than the report's own AngularRmsErrorDegrees, which fits out
 * sign and scale before measuring. That fit is the right way to score a unit, but it is the wrong way to
 * draw one — it would hide exactly the thing this picture is for, which is that the large errors are all
 * out at the edges of the sweep where the return has dropped into the sidelobes.
 */
export const azimuthErrorDegrees = (point: FovSweepPoint): number =>
    point.detected && Number.isFinite(point.measuredAzimuthDegrees)
        ? Math.abs(point.measuredAzimuthDegrees - point.azimuthDegrees)
        : NaN;

/** Smallest and largest finite value produced by <paramref name="selector"/>, or undefined if none are. */
export const finiteExtent = (
    points: FovSweepPoint[],
    selector: (point: FovSweepPoint) => number,
): { min: number; max: number } | undefined => {
    let min = Infinity;
    let max = -Infinity;

    points.forEach((p) => {
        const value = selector(p);
        if (!Number.isFinite(value)) return;
        if (value < min) min = value;
        if (value > max) max = value;
    });

    return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : undefined;
};
